import { format, parseISO } from 'date-fns'
import { sv as svLocale } from 'date-fns/locale'

/**
 * Format number as Swedish currency (SEK)
 * 1234.56 -> "1 234 kr"
 * With compact=true: 1234567 -> "1,2M kr"
 */
export const formatSEK = (value: number | null | undefined, compact: boolean = false): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0 kr'
  }

  if (compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })
      .format(value)
      .replace('SEK', 'kr')
      .trim()
  }

  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace('SEK', 'kr')
    .trim()
}

/**
 * Format number as Swedish currency with decimals
 * 1234.56 -> "1 234,56 kr"
 */
export const formatSEKWithDecimals = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0,00 kr'
  }

  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value)
    .replace('SEK', 'kr')
    .trim()
}

/**
 * Format number with Swedish locale
 * 1234.56 -> "1 234,56"
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0'
  }

  return new Intl.NumberFormat('sv-SE').format(value)
}

/**
 * Format percentage
 * 0.125 -> "12,5%"
 */
export const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }

  return new Intl.NumberFormat('sv-SE', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value)
}

/**
 * Format percentage from whole number
 * 12.5 -> "12,5%"
 */
export const formatPercentFromWhole = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }

  return `${formatNumber(value)}%`
}

/**
 * Format date to Swedish format
 * Date -> "2024-01-31"
 */
export const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    
    // Check if the date is valid
    if (!dateObj || isNaN(dateObj.getTime())) {
      return ''
    }
    
    return format(dateObj, 'yyyy-MM-dd')
  } catch (error) {
    console.warn('Invalid date passed to formatDate:', date, error)
    return ''
  }
}

/**
 * Format date with time
 * Date -> "2024-01-31 14:30"
 */
export const formatDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  
  try {
    // Handle case where date is an object but not a Date
    if (typeof date === 'object' && !(date instanceof Date)) {
      console.warn('Invalid date object passed to formatDateTime:', date)
      return ''
    }
    
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    
    // Check if the date is valid
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return ''
    }
    
    return format(dateObj, 'yyyy-MM-dd HH:mm')
  } catch (error) {
    console.warn('Invalid date passed to formatDateTime:', date, error)
    return ''
  }
}

/**
 * Format date to friendly Swedish format
 * Date -> "31 januari 2024"
 */
export const formatDateFriendly = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    
    // Check if the date is valid
    if (!dateObj || isNaN(dateObj.getTime())) {
      return ''
    }
    
    return format(dateObj, 'd MMMM yyyy', { locale: svLocale })
  } catch (error) {
    console.warn('Invalid date passed to formatDateFriendly:', date, error)
    return ''
  }
}

/**
 * Format relative time
 * Date -> "3 dagar sedan"
 */
export const formatRelativeTime = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    
    // Check if the date is valid
    if (!dateObj || isNaN(dateObj.getTime())) {
      return ''
    }
    
    const now = new Date()
    const diffInMs = now.getTime() - dateObj.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    
    if (diffInDays === 0) return 'Idag'
    if (diffInDays === 1) return 'Igår'
    if (diffInDays < 7) return `${diffInDays} dagar sedan`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} veckor sedan`
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} månader sedan`
    return `${Math.floor(diffInDays / 365)} år sedan`
  } catch (error) {
    console.warn('Invalid date passed to formatRelativeTime:', date, error)
    return ''
  }
}

/**
 * Parse Swedish formatted number to float
 * "1 234,56" -> 1234.56
 */
export const parseSwedishNumber = (value: string): number => {
  if (!value) return 0
  
  // Remove spaces (thousand separator) and replace comma with dot (decimal separator)
  const cleaned = value
    .replace(/\s/g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '') // Remove any non-numeric characters except dot and minus
  
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Format driver name
 * "JOHN DOE" -> "John Doe"
 */
export const formatDriverName = (name: string | null | undefined): string => {
  if (!name) return 'Okänd Förare'
  
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Format file size
 * 1024 -> "1 KB"
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  
  const units = ['B', 'KB', 'MB', 'GB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${units[i]}`
}

/**
 * Truncate text with ellipsis
 * "Very long text" -> "Very lo..."
 */
export const truncate = (text: string, maxLength: number = 50): string => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Calculate percentage change
 * (100, 120) -> "+20%"
 */
export const calculatePercentageChange = (oldValue: number, newValue: number): string => {
  if (oldValue === 0) return newValue > 0 ? '+100%' : '0%'
  
  const change = ((newValue - oldValue) / oldValue) * 100
  const formatted = formatNumber(Math.abs(change))
  
  if (change > 0) return `+${formatted}%`
  if (change < 0) return `-${formatted}%`
  return '0%'
}

/**
 * Export data to CSV file
 */
export const exportToCSV = (data: any[], filename: string = 'export.csv', originalHeaders?: string[]) => {
  if (!data.length) return

  // Use original headers if provided, otherwise fallback to Object.keys()
  const headers = originalHeaders && originalHeaders.length > 0 ? originalHeaders : Object.keys(data[0])
  
  // Create CSV content
  const csvContent = [
    // Headers row
    headers.join(','),
    // Data rows
    ...data.map(row => 
      headers.map(header => {
        const value = row[header]
        // Wrap in quotes if contains comma or newline, escape existing quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }).join(',')
    )
  ].join('\n')

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

/**
 * Export data to Excel file using XLSX
 */
export const exportToExcel = async (data: any[], filename: string = 'export.xlsx', originalHeaders?: string[]) => {
  if (!data.length) return

  // Dynamic import to avoid loading XLSX unless needed
  const XLSX = await import('xlsx')

  // Use original headers if provided, otherwise fallback to Object.keys()
  const headers = originalHeaders && originalHeaders.length > 0 ? originalHeaders : Object.keys(data[0])

  // Create worksheet with specific column order
  const ws = XLSX.utils.json_to_sheet(data, { header: headers })

  // Auto-size columns
  const cols = headers.map(key => ({
    wch: Math.max(
      key.length,
      ...data.map(row => String(row[key] || '').length)
    ) + 2
  }))
  ws['!cols'] = cols

  // Create workbook
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Data')

  // Write file
  XLSX.writeFile(wb, filename)
}

export const exportToPDF = async (data: any[], filename: string = 'export.pdf', headers: string[]) => {
  if (!data.length || !headers.length) return

  try {
    // Dynamic import to avoid loading jsPDF unless needed
    const { jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    // Landscape mode for wider tables
    const doc = new jsPDF({
      orientation: 'landscape',
      format: 'a4',
      compress: true
    })

    // Convert data to array format for autoTable
    const body = data.map(row =>
      headers.map(header => row[header])
    )

    // Use functional API for autoTable with dynamic imports
    autoTable(doc, {
      head: [headers],
      body: body,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [66, 66, 66],
        textColor: 255
      },
      // Split table across multiple pages horizontally if too many columns
      horizontalPageBreak: true,
      horizontalPageBreakRepeat: 0 // Repeat first column (usually ID or name) on each page
    })

    doc.save(filename)
  } catch (error) {
    console.error('PDF export failed:', error)
    throw new Error('Kunde inte skapa PDF-fil. Försök igen.')
  }
}